import React, { useState, useEffect, useRef } from 'react';
import { Info, ExternalLink, ChevronDown, ChevronUp, ArrowRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/Card';
import { Tooltip } from '../ui/Tooltip';
import { cn } from '../../utils/cn';
import { usePoliceFeedStore } from '../../stores/usePoliceFeedStore';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  date: string;
  url: string;
  district?: string | null;
}

interface NewsFeedProps {
  className?: string;
}

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('no', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).replace(/\./g, '.');
};

// Helper function to clean title (remove ID part)
const cleanTitle = (title: string): string => {
  return title.replace(/\s*\(ID:\s*[^)]+\)\s*$/, '');
};

export function NewsFeed({ className }: NewsFeedProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [residenceCity, setResidenceCity] = useState<string | null>(null);
  const lastCityRef = useRef<string | null>(null);
  const { currentResidence } = useUser();
  const { items: newsItems, loading, error: feedError, fetchPoliceFeed, clearError } = usePoliceFeedStore();

  // Hent bofellesskapets by
  useEffect(() => {
    const fetchResidenceCity = async () => {
      if (!currentResidence?.residence_id) return;

      try {
        const { data: residenceData, error } = await supabase
          .from('residence')
          .select('city')
          .eq('id', currentResidence.residence_id)
          .single();

        if (error) {
          // ignorer
          return;
        }

        if (residenceData?.city) {
          if (residenceCity !== residenceData.city) {
            setResidenceCity(residenceData.city);
          }
        } else {
          if (residenceCity !== null) setResidenceCity(null);
        }
      } catch {
        // ignorer
      }
    };

    fetchResidenceCity();
  }, [currentResidence?.residence_id, residenceCity]);

  // Hent politiet-feed data (unngå gjentatte fetch for samme by)
  useEffect(() => {
    if (residenceCity === lastCityRef.current) return;
    lastCityRef.current = residenceCity;
    fetchPoliceFeed(residenceCity || undefined);
  }, [residenceCity, fetchPoliceFeed]);

  // visibleNews ikke i bruk; vi bruker displayVisibleNews under

  // Fallback til statiske nyheter hvis ingen data eller feil
  const fallbackNews: NewsItem[] = [
    {
      id: '1',
      title: 'Ingen nyheter tilgjengelig',
      content: 'Vi henter nyheter fra politiet for ditt område. Prøv igjen senere.',
      date: formatDate(new Date().toISOString()),
      url: '#'
    }
  ];

  const displayNews = newsItems.length > 0 ? newsItems : fallbackNews;
  const displayVisibleNews = isExpanded ? displayNews : displayNews.slice(0, 3);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle size="lg">Nyheter fra nærområdet</CardTitle>
          <Tooltip content={
            <div className="whitespace-pre-line">
              Condo henter nyheter fra Politiet.no.
              {residenceCity && ` Filtrert for ${residenceCity}.`}
            </div>
          }>
            <Info 
              className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors cursor-help"
              aria-label="Informasjon om nyhetskilden"
            />
          </Tooltip>
        </div>
      </CardHeader>
      
      {feedError && (
        <div className="p-3 bg-red-50 border-b border-red-100">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{feedError}</span>
            <button 
              onClick={clearError}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <CardContent>
        <div className="divide-y divide-gray-100 text-base sm:text-md p-0 sm:p-2">
          {loading && newsItems.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              <div className="animate-pulse">Henter nyheter...</div>
            </div>
          ) : (
            displayVisibleNews.map((item: NewsItem) => (
              <article 
                key={item.id} 
                className="pt-2 pb-3 sm:p-3 hover:bg-gray-50 transition-colors"
                role="article"
              >
                <a 
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h3 className="text-sm sm:text-md font-medium sm:mb-1 group-hover:text-condo-dark flex items-center gap-1">
                        {cleanTitle(item.title)}
                        <ExternalLink 
                          className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-hidden="true"
                        />
                      </h3>
                      <p className="text-sm sm:text-md text-gray-600 line-clamp-2 mb-1">
                        {item.content}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <time dateTime={item.date}>
                          {item.date}
                        </time>
                        {item.district && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {item.district}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              </article>
            ))
          )}
        </div>
        
        {newsItems.length > 0 && (
          <div className="flex justify-end sm:mt-4">
            {/* Mobile "se mer" link */}
            <Link 
              to="/" 
              className={cn(
                "flex items-center gap-1 text-xs text-condo-dark hover:text-condo-med transition-colors sm:hidden m-1",
                "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded"
              )}
            >
              <span>Les flere nyheter</span>
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        )}

        {/* Desktop "se mer" button */}
        {newsItems.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "w-full p-3 text-sm text-gray-600 hover:text-gray-900 hidden sm:flex items-center justify-center gap-2 border-t",
              "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2"
            )}
            aria-expanded={isExpanded}
            aria-controls="news-feed"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
                <span>Vis mindre</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                <span>Vis mer</span>
              </>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
}